import {config} from '@app/modules/config';
import type {OffersAcquisitionResponse} from '@app/types/offersAcq';
import {getLegacyLocaleFromLocale} from '@deezer/deezer-compatibility';

export const getPlanId = (plan: string) => {
	switch (plan) {
		case 'premium':
			return 1000001;
		case 'duo':
			return 1000002;
		case 'family':
			return 1000006;
		case 'student':
			return 1005001;
		default:
			return '';
	}
};

export const getOffersPrice = (
	plan: string,
	offersData: OffersAcquisitionResponse,
) => {
	return offersData
		.filter((offer) => offer.public && getPlanId(plan) === offer.offer_id)
		.map((offer) => offer.subscription_period_collection.initial.price);
};

export const getOfferMonthNumber = (
	plan: string,
	offersData: OffersAcquisitionResponse,
) => {
	return offersData
		.filter((offer) => offer.public && getPlanId(plan) === offer.offer_id)
		.map((offer) => offer.subscription_period_collection.try_and_buy.duration);
};

export const getOfferColor = (plan: string) => {
	switch (plan) {
		case 'premium':
			return '#a238ff';
		case 'duo':
			return '#96f9f3';
		case 'family':
			return '#ff673d';
		case 'student':
			return '#a238ff';
		default:
			return '#a238ff';
	}
};

export const getPlanTitle = (
	plan: string,
	offersData: OffersAcquisitionResponse,
) => {
	return offersData
		.filter((offer) => offer.public && getPlanId(plan) === offer.offer_id)
		.map((offer) => offer.label);
};

export const getOffersInfoByPlan = (
	plan: 'duo' | 'family' | 'premium' | 'student',
	offersData: OffersAcquisitionResponse,
) => {
	return offersData
		.filter((offer) => offer.public && getPlanId(plan) === offer.offer_id)
		.map((offer) => ({
			initialPrice: offer.subscription_period_collection.initial.price,
			offerId: offer.offer_id,
			duration: offer.subscription_period_collection.try_and_buy.duration,
			label: offer.label,
		}))[0];
};

export const getOffersYearlyInfoByPlan = (
	plan: 'duo' | 'family' | 'premium' | 'student',
	offersData: OffersAcquisitionResponse,
) => {
	return offersData
		.filter((offer) => getPlanId(plan) === offer.offer_id)
		.map((offer) => ({
			initialPrice: offer?.subscription_period_collection?.initial.price ?? '-',
			price_by_month:
				offer?.subscription_period_collection?.initial?.price_by_month ?? '-',
			duration:
				offer?.subscription_period_collection?.try_and_buy?.duration ?? '-',
			label: offer.label ?? 'premium',
		}))[0];
};

/**
 * Returns the country code for a given locale.
 *
 * @param locale - The locale.
 * @param locales - The available locale list.
 */
export const getCountryFromLocale = (
	locale: string,
	locales: string[],
): string => {
	if (!locales.includes(locale)) {
		throw new Error(
			`i18n: the locale "${locale}" is invalid or not supported.`,
		);
	}
	return getLegacyLocaleFromLocale(locale);
};

export const fetchOffers = async ({
	locale,
	locales,
}: {
	locale: string;
	locales: string[];
}) => {
	const geoipFromLocale = getCountryFromLocale(locale, locales);
	const response = await fetch(
		`
		${config.get('deezerApiUrl')}/ajax/gw-light.php?method=payment_getPlanCatalog&input=3&api_version=1.0&api_token=&cid=442313930&GEOIP=${geoipFromLocale}
		`,
		{
			method: 'POST',
		},
	);

	const data = await response.json();

	return data;
};

export const getTrackAcqPageName = (path: string) => {
	switch (path) {
		case '/offers/premium/acq/':
			return 'premium-acq';
		case '/offers/family/acq/':
			return 'family-acq';
		case '/offers/annual-plan/acq/':
			return 'annual-plan-acq';
		case '/offers/duo/acq/':
			return 'duo-acq';
		case '/offers/family/acq-2/':
			return 'family-acq-v2';
		case '/offers/premium/acq-2/':
			return 'premium-acq-v2';
		case '/offers/student/acq/':
			return 'student-acq';
		default:
			return '';
	}
};
